import type { MerchantPermissionCode } from '@kardume/types';

/** Admin (merchant) navigation model with per-section RBAC. */

export type AdminSectionId =
  | 'dashboard'
  | 'promotions'
  | 'experiences'
  | 'coupons'
  | 'customers'
  | 'reviews'
  | 'cashback'
  | 'billing'
  | 'reports'
  | 'employees'
  | 'access-profiles'
  | 'settings'
  | 'profile'
  | 'business-hours';

export type AdminNavGroup =
  | 'Visão geral'
  | 'Marketing'
  | 'Relacionamento'
  | 'Financeiro'
  | 'Equipe'
  | 'Conta';

export interface AdminNavItem {
  section: AdminSectionId;
  group: AdminNavGroup;
  label: string;
  /** Any of these permissions grants access (OWNER always passes). */
  permissions: MerchantPermissionCode[];
}

export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { section: 'dashboard', group: 'Visão geral', label: 'Dashboard', permissions: ['ROLE_MERCHANT_DASHBOARD_READ'] },
  { section: 'promotions', group: 'Marketing', label: 'Anúncios', permissions: ['ROLE_MERCHANT_PROMOTION_READ', 'ROLE_MERCHANT_PROMOTION_MANAGE'] },
  { section: 'coupons', group: 'Marketing', label: 'Cupons', permissions: ['ROLE_MERCHANT_COUPON_READ', 'ROLE_MERCHANT_COUPON_MANAGE'] },
  // Card #187 - em Marketing, ao lado de Anúncios e Cupons: é o que a loja
  // oferece para atrair alguém, com a diferença de que experiência tem agenda,
  // vaga e participante. Permissão própria (READ para ver a agenda e a lista da
  // noite; MANAGE para mexer) - quem trabalha no salão precisa ver quem vem sem
  // poder cancelar data.
  { section: 'experiences', group: 'Marketing', label: 'Experiências', permissions: ['ROLE_MERCHANT_EXPERIENCE_READ', 'ROLE_MERCHANT_EXPERIENCE_MANAGE'] },
  { section: 'customers', group: 'Relacionamento', label: 'Clientes', permissions: ['ROLE_MERCHANT_CUSTOMER_READ'] },
  { section: 'reviews', group: 'Relacionamento', label: 'Avaliações', permissions: ['ROLE_MERCHANT_REVIEW_READ', 'ROLE_MERCHANT_REVIEW_MANAGE'] },
  { section: 'cashback', group: 'Relacionamento', label: 'Cashback', permissions: ['ROLE_MERCHANT_CASHBACK_MANAGE'] },
  { section: 'billing', group: 'Financeiro', label: 'Financeiro', permissions: ['ROLE_MERCHANT_FINANCE_READ'] },
  { section: 'reports', group: 'Financeiro', label: 'Relatórios', permissions: ['ROLE_MERCHANT_REPORT_READ'] },
  { section: 'employees', group: 'Equipe', label: 'Funcionários', permissions: ['ROLE_MERCHANT_EMPLOYEE_READ', 'ROLE_MERCHANT_EMPLOYEE_MANAGE'] },
  { section: 'access-profiles', group: 'Equipe', label: 'Perfis & Alçadas', permissions: ['ROLE_MERCHANT_ACCESS_PROFILE_MANAGE'] },
  { section: 'settings', group: 'Conta', label: 'Configurações', permissions: ['ROLE_MERCHANT_PROFILE_MANAGE'] },
  { section: 'profile', group: 'Conta', label: 'Perfil da loja', permissions: ['ROLE_MERCHANT_PROFILE_MANAGE'] },
  // Item próprio, e não uma aba do perfil: horário é dado operacional que muda
  // toda semana, tem gravação própria (substitui a semana inteira) e alçada
  // própria - o gerente de turno mexe no horário sem poder mexer no CNPJ.
  { section: 'business-hours', group: 'Conta', label: 'Horários', permissions: ['ROLE_MERCHANT_BUSINESS_HOURS_MANAGE'] },
];

export const ADMIN_NAV_GROUP_ORDER: AdminNavGroup[] = [
  'Visão geral',
  'Marketing',
  'Relacionamento',
  'Financeiro',
  'Equipe',
  'Conta',
];

export const isAdminSection = (value: string | null): value is AdminSectionId =>
  !!value && ADMIN_NAV_ITEMS.some((item) => item.section === value);

export const hasAnyPermission = (
  available: readonly string[] | undefined,
  required: readonly string[],
  isOwner = false
): boolean => {
  if (isOwner) return true;
  if (required.length === 0) return true;
  if (!available || available.length === 0) return false;
  return required.some((code) => available.includes(code));
};

export const getVisibleNavItems = (available: readonly string[] | undefined, isOwner = false) =>
  ADMIN_NAV_ITEMS.filter((item) => hasAnyPermission(available, item.permissions, isOwner));

export const getInitialSection = (
  available: readonly string[] | undefined,
  preferred: AdminSectionId | null,
  isOwner = false
): AdminSectionId | null => {
  const visible = getVisibleNavItems(available, isOwner);
  if (preferred && visible.some((item) => item.section === preferred)) return preferred;
  return visible[0]?.section ?? null;
};

export const isSectionVisible = (
  available: readonly string[] | undefined,
  section: AdminSectionId,
  isOwner = false
) => getVisibleNavItems(available, isOwner).some((item) => item.section === section);
